// src/lib/events.ts — front-end client for the Events Live volunteer loop.
// Calls InfoHub's own /api/events-live/* proxy (resources/events-live.ts), which
// forwards to the Events Manager server-to-server. Shapes mirror the Events API
// (volunteerLoop.ts) payloads.

import { apiFetch } from "../api";

export interface ELEvent {
  id: number;
  name: string;
  typeName: string;
  typeColourBg: string;
  typeColourText: string;
  days: string[];           // ISO YYYY-MM-DD
  location: string | null;
  summary: string | null;
}

export interface ELRole {
  id: number;
  slug: string;
  name: string;
  start: string | null;     // "HH:mm"
  end: string | null;
  need: number | null;
  duties: string[];
  bring: string[];
}

export interface ELCall {
  id?: number;
  eventId: number;
  message: string | null;
  audience: string | null;
  isActive: boolean;
  publishedAt?: string | null;
  closedAt?: string | null;
  roleIds: number[];
}

export type ELOfferStatus = "pending" | "approved" | "declined";

export interface ELOffer {
  mode: "whole" | "days";
  note: string | null;
  readConfirmed: boolean;
  anyRole?: boolean;          // offered for any role (item 3)
  status: ELOfferStatus;
  submittedAt: string;
  roleIds: number[];
  days: string[];
}

export type ELAssignmentStatus = "assigned" | "confirmed";

export interface ELAssignment {
  roleId: number;
  roleName: string;
  date: string;
  status: ELAssignmentStatus;
  start: string | null;
  end: string | null;
}

// GET /api/events-live/open-call — drives the Home banner state machine
// (call → pending/approved → live) and the Crew Call page.
export type ELEventState = "call" | "pending" | "live" | null;

// One relevant event for the caller (an active call, or one they're involved in).
// A volunteer can have several at once — events[] carries them all, each with its
// own banner state + the detail the Crew Call / Live picker need.
export interface ELEventEntry {
  event: ELEvent;
  call: ELCall | null;
  roles: ELRole[];
  myOffer: ELOffer | null;
  myAssignments: ELAssignment[];
  myConfirmed: ELAssignment[];
  signedOn: boolean;
  signedOnAt: string | null;
  state: ELEventState;
}

export interface OpenCallResponse {
  call: ELCall | null;
  event?: ELEvent;
  roles?: ELRole[];
  myOffer?: ELOffer | null;
  myAssignments?: ELAssignment[];
  myConfirmed?: ELAssignment[];
  signedOn?: boolean;
  signedOnAt?: string | null;
  // All relevant events for the caller (multi-event). Top-level fields above
  // mirror events[0] (the primary) for backward-compat.
  events?: ELEventEntry[];
}

export interface ELLiveAssignment extends ELAssignment {
  note: string | null;
  duties: string[];
}

export interface ELBriefing {
  topicId: number;
  title: string;
  category: string;
  durationMinutes: number;
  bodyMarkdown: string;
  version: number;
  ackState: "none" | "acked" | "stale";
}

export interface ELScheduleItem {
  id: number;
  label: string;
  time: string;
  loco: string | null;
  origin: string | null;
  destination: string | null;
}

export interface ELContact {
  name: string | null;
  role: string;
  phone: string | null;
}

export interface ELFeedItem {
  id: number;
  occurredAt: string;
  summary: string;
  actorName: string | null;
}

// GET /api/events-live/live?eventId= — the caller's Live Duty View payload.
export interface LivePayload {
  now: string;
  event: { id: number; name: string };
  days: string[];
  targetDate: string;
  dayOfEvent: number | null;
  totalDays: number;
  within: boolean;
  hasConfirmedRole: boolean;
  myAssignments: ELLiveAssignment[];
  myConfirmed: ELLiveAssignment[];
  signOn: { isSignedOn: boolean; signedOnAt: string | null; signedOnDay: number | null };
  briefings: ELBriefing[];
  schedule: ELScheduleItem[];
  contacts: ELContact[];
  feed: ELFeedItem[];
}

export interface SubmitOfferBody {
  eventId: number;
  mode: "whole" | "days";
  dayDates: string[];
  roleIds: number[];
  anyRole?: boolean;          // offer for whatever's needed (item 3)
  note?: string | null;
  readConfirmed: boolean;
}

export function fetchOpenCall(): Promise<OpenCallResponse> {
  return apiFetch<OpenCallResponse>("/api/events-live/open-call");
}

export function submitOffer(body: SubmitOfferBody): Promise<{ ok: boolean; status: string }> {
  return apiFetch("/api/events-live/offer", { method: "POST", body: JSON.stringify(body) });
}

// Withdraw from the event entirely (caller unchecked every role on Crew Call).
// Removes their offer + any assignments in Events; the Home banner reverts to
// the original "Volunteers needed" call invitation.
export function withdrawOffer(eventId: number): Promise<{ ok: boolean; removedAssignments: number; hadOffer: boolean }> {
  return apiFetch("/api/events-live/withdraw", { method: "POST", body: JSON.stringify({ eventId }) });
}

export function fetchLive(eventId: number): Promise<LivePayload> {
  return apiFetch<LivePayload>(`/api/events-live/live?eventId=${eventId}`);
}

export function signOn(eventId: number): Promise<unknown> {
  return apiFetch("/api/events-live/sign-on", { method: "POST", body: JSON.stringify({ eventId }) });
}

export function signOff(eventId: number): Promise<unknown> {
  return apiFetch("/api/events-live/sign-off", { method: "POST", body: JSON.stringify({ eventId }) });
}

export function ackBriefing(eventId: number, topicId: number): Promise<unknown> {
  return apiFetch("/api/events-live/ack", { method: "POST", body: JSON.stringify({ eventId, topicId }) });
}
